import { z } from 'zod';

/**
 * Validation schema for note create/update — same shape for both per the API
 * (`CreateNoteRequest`/`UpdateNoteRequest` are identical).
 */
export const noteFormSchema = z.object({
  title: z.string().max(200, { error: 'notes.form.validation.titleTooLong' }).nullable().optional(),
  content: z
    .string()
    .min(1, { error: 'notes.form.validation.contentRequired' })
    .max(100_000, { error: 'notes.form.validation.contentTooLong' }),
});

export type NoteFormValues = z.infer<typeof noteFormSchema>;

/**
 * Validation schema for the convert-to-task dialog form.
 * Project and board are required; all other fields are optional.
 */
export const convertToTaskSchema = z.object({
  organizationId: z.string().optional(),
  title: z
    .string()
    .min(1, { error: 'notes:convertToTask.validation.titleRequired' })
    .max(200, { error: 'notes:convertToTask.validation.titleTooLong' }),
  description: z.string().nullable().optional(),
  // `notes:` namespace prefix — these messages are rendered, so they must resolve
  // through i18next from any namespace, unlike the safeParse-only note schema above.
  projectId: z.string().min(1, { error: 'notes:convertToTask.validation.projectRequired' }),
  boardId: z.string().min(1, { error: 'notes:convertToTask.validation.boardRequired' }),
  statusId: z.string().optional(),
  groupId: z.string().nullable().optional(),
  // Tasks.Domain Priority enum — see shared/lib/priority.ts. 0 (none) is expressed
  // as "no selection", so the field is absent rather than zero.
  priority: z.number().min(1).max(4).optional(),
  // Opaque id, not a uuid: the picker's options carry the project member's `userId`, which
  // is the backend's numeric id. A uuid check rejected every assignee the user picked and
  // blocked the submit; membership is validated server-side anyway.
  assigneeId: z.string().nullable().optional(),
  dueDate: z.date().nullable().optional(),
  startDate: z.date().nullable().optional(),
  estimatedMinutes: z.number().min(0).nullable().optional(),
  tags: z.array(z.string()).optional(),
});

export type ConvertToTaskFormValues = z.infer<typeof convertToTaskSchema>;
